/*
 * Copyright (C) 2005 - 2023. Cloud Software Group, Inc. All Rights Reserved. Confidential & Proprietary.
 * Licensed pursuant to commercial Cloud Software Group, Inc. End User License Agreement.
 */

import { ILinkOptions } from 'src/adhoc/store/types/StateTypes';
import { RefObject } from 'react';
import {
    IVisualizationDynamicProperties,
    IVisualizationStaticProperties
} from '../../../../store/types/VisualizationTypes';
import {
    IMultiLevelDataset,
    IMultiLevelDatasetHandle,
    IMultiLevelDatasetVisualization
} from '../../../../store/types/MultiLevelDatasetTypes';
import { Nullable } from '../../../../store/types/CommonTypes';

import { ILinkToRefObjectPair, ILinkData } from '../../types/commonTypes';

export interface ITableVisualizationDynamicProperties extends IVisualizationDynamicProperties {
    headerHeight: number,
    bottomHeight: number,
    tableContainerHeight: number,
    contentHeight: number,
    offsetTop: number,
    rowHeights: number[],
    viewPortHeight: number,
    minRowHeight: number,
    rowsCountToRender: number,
}

export interface IGroup {
    id: string,
    fieldName: string,
    hierarchicalFieldName: string,
    kind: 'level',
    label: string,
    labelId: Nullable<string>,
    groupType: string,
    groupFormat?: string
}

export interface ITableVisualizationStaticProperties extends IVisualizationStaticProperties {
    showDetails: boolean,
    showTotals: boolean,
    distinctFields: boolean,
    columns: IColumn[]
    groups: IGroup[],
    order: any[]
}

export interface ITableVisualizationWrapperProps extends IMultiLevelDatasetVisualization {
    dataset: IMultiLevelDataset,
    handle: IMultiLevelDatasetHandle,
    linkOptions: ILinkOptions,
    staticProperties: ITableVisualizationStaticProperties,
    dynamicProperties: ITableVisualizationDynamicProperties
}

export interface ICell {
    id: string,
    label: string,
    width: number,
    collSpan?: number,
    contentType?: string,
    linkData?: ILinkData,
    tableLink?: ITableLink,
    aggregationLabel?: string
}

export interface IHeader {
    cells: ICell[],
}

export interface IBottom {
    cells: ICell[]
}

// Rows

enum RowType {
    DETAIL = 'DETAIL',
    GROUP = 'GROUP',
    GROUP_TOTAL = 'GROUP_TOTAL',
    GRANT_TOTAL = 'GRANT_TOTAL'
}

export { RowType };

export interface IRow {
    id: string,
    index: number,
    rowType: RowType,
    height: number,
    cells: ICell[],
    isLoading?: boolean
    fieldDisplay?: string
}

export interface ITable {
    header: IHeader,
    rows: IRow[],
    bottom: IBottom
}

export type IColumnKind = 'level' | 'measure';
export type IColumnType = 'column' | 'spacer';

export interface IColumn {
    id: string,
    fieldName: string,
    hierarchicalFieldName: string,
    width: number,
    label: string,
    aggregationFunction?: string
    aggregationType?: string,
    aggregationFormat?: string,
    kind: IColumnKind,
    type: IColumnType,
    timeBalanceFunctionName?: string
    detailType?: string,
    detailFormat?: string,
    showSummary: boolean
}

export interface ITableComponentProps {
    rows: IRow[];
    offsetTop: number;
    linkToRefObjectPairs: ILinkToRefObjectPair[],
    linkOptions: ILinkOptions
}

export interface IBodyContainerProps {
    getRef: (ref: RefObject<HTMLDivElement>) => void,
    rows: IRow[],
    dynamicProperties: ITableVisualizationDynamicProperties,
    staticProperties: ITableVisualizationStaticProperties,
    onScrollCallback: (event: any) => void,
    onUpdateDynamicProperties: (dynamicProperties: ITableVisualizationDynamicProperties) => void,
    linkOptions: ILinkOptions,
    linkToRefObjectPairs: ILinkToRefObjectPair[]
}

export interface ITableCellComponentProps {
    width: number;
    title: string;
    contentType?: string,
    aggregationLabel?: string,
    colSpan?: number;
    cellRef?: RefObject<any>,
    cellIndex: number,
    eventListener: (event: any) => void
}

export interface IHeaderContainerComponentProps {
    getRef: (ref: RefObject<HTMLDivElement>) => void,
    header: IHeader,
    linkOptions: ILinkOptions,
    linkToRefObjectPairs: ILinkToRefObjectPair[]
}

export interface IBottomContainerComponentProps {
    getRef: (ref: RefObject<HTMLDivElement>) => void,
    bottom: IBottom,
    bottomOffset: number,
    useScroll: boolean,
    onScrollCallback: (event: any) => void,
    linkOptions: ILinkOptions,
    linkToRefObjectPairs: ILinkToRefObjectPair[]
}

export interface ITableLink {
    id?: string,
    value?: string,
    linkType: LinkType,
    group?: any,
    column?: any,
    row?: any
}

enum LinkType {
    HEADER = 'HEADER',
    DATA = 'DATA',
    GROUP_LABEL = 'GROUP_LABEL',
    GROUP_TOTAL = 'GROUP_TOTAL',
    GRAND_TOTAL = 'GRAND_TOTAL'
}

export { LinkType };
